import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { FillStyle, LineStyle } from '@/api/types/stowed-element'
import {
  SHAPE_DEFAULT_FILL_COLOR_ID,
  SHAPE_DEFAULT_FILL_TINT,
} from '../../constants/shape-default-fill-color-id'

// yb360 renders a shape only if it carries paint. larry stores a shape's fill/stroke as CSS strings
// in the frontend-only `editorShape` (which the backend drops), so unless we emit an explicit wire
// fill/stroke the shape recomposes with no paint and renders invisibly in yb360 — you see only its
// text. No client-side colorId→RGB palette exists yet (the Load-Service color library is
// unimplemented), so we emit a known-visible default palette color/tint to make the shape appear;
// exact CSS-color→palette fidelity is a follow-up. CI=2 is the dark fill color used by text runs in
// the sample NSD fixtures; a low tint over it yields a light gray that keeps embedded text readable.
export { SHAPE_DEFAULT_FILL_COLOR_ID, SHAPE_DEFAULT_FILL_TINT }

export const SHAPE_DEFAULT_STROKE_COLOR_ID = 2

export const SHAPE_DEFAULT_STROKE_TINT = 100

/**
 * A CSS color is "visible" unless it is explicitly transparent/none/empty. An undefined fill still
 * renders as the default gray in larry (`shape.fill || '#e5e7eb'` in shape-renderer), so fill
 * callers treat undefined as visible; an undefined stroke has no default color, so stroke callers do not.
 */
export function isVisibleCssColor(
  value: string | undefined,
  treatUndefinedAsVisible: boolean
): boolean {
  if (value == null) return treatUndefinedAsVisible
  const v = value.trim().toLowerCase()

  return v !== '' && v !== 'transparent' && v !== 'none'
}

export function buildLineStyle(shape: ShapeData): LineStyle | undefined {
  if (shape.strokeWidth == null && shape.stroke == null) return undefined

  const lineStyle: LineStyle = { weight: shape.strokeWidth ?? 1 }

  // Emit a palette stroke color so a bordered shape shows its outline in yb360 (weight alone is
  // colorless). Skipped when the stroke is explicitly transparent/none.
  if (isVisibleCssColor(shape.stroke, false)) {
    lineStyle.colorId = SHAPE_DEFAULT_STROKE_COLOR_ID
    lineStyle.tint = SHAPE_DEFAULT_STROKE_TINT
  }

  return lineStyle
}

/**
 * Palette fill for a shape with a visible fill so the shape renders in yb360 rather than showing
 * only its text. Returns undefined for a photo-filled shape (don't paint over the photo) or an
 * explicitly transparent/none fill.
 */
export function buildShapeFillStyle(shape: ShapeData): FillStyle | undefined {
  if (shape.photoFill) return undefined
  if (!isVisibleCssColor(shape.fill, true)) return undefined

  return {
    colorId: shape.fillColorId ?? SHAPE_DEFAULT_FILL_COLOR_ID,
    tint: shape.fillTint ?? SHAPE_DEFAULT_FILL_TINT,
  }
}
